import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { ClassProvider, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Company } from '../../../auth/models';
import { AuthService, JwtService } from '../../../auth/services';
import { rememberNext } from '../../../auth/access/access-next';
import { ConnectionMonitorService } from '../../services/connection-monitor/connection-monitor.service';
import { ErrorPagesService, hasUnsavedInput } from '../../../v2/errors/error-pages.service';
import { ErrorPageRoute, companySwitchedName, errorPageFor, markPageLoad } from '../../../v2/errors/http-error-routing';
import { CompanySwitchedService } from '../../../v2/shell/v2-company-switched.component';
import { documentRequiredUrl } from '../../../v2/pages/documents/document-block';


/**
 * A 401: the session is gone. Off /access, always go to sign-in; only a user who had a
 * token gets the page remembered and the "session expired" notice. During a navigation
 * (a resolver 401) the page remembered is the one being opened, not the one left.
 */
export function onUnauthorized(router: Router, auth: AuthService, hadToken: boolean): void {
    auth.logout();
    if (router.url.startsWith('/access')) return;
    // A parallel 401 whose first call already started the move to /access: leave it be.
    const pending = router.currentNavigation()?.finalUrl;
    if (pending && router.serializeUrl(pending).startsWith('/access')) return;
    if (!hadToken) {
        router.navigate(['/access/login']);
        return;
    }
    const target = router.currentNavigation()?.finalUrl;
    rememberNext(target ? router.serializeUrl(target) : router.url);
    router.navigate(['/access/login'], { queryParams: { notice: 'expired' } });
}

/** True only for our own API (environment.apiUrl), never a look-alike host or another origin. */
export function isApiUrl(url: string): boolean {
    const base = environment.apiUrl.replace(/\/+$/, '');
    if (typeof url !== 'string' || !base || !url.startsWith(base)) {
        return false;
    }
    const next = url.charAt(base.length);
    return next === '' || next === '/' || next === '?' || next === '#';
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private _authService: AuthService;
    private _jwtService: JwtService;
    private _connectionMonitor: ConnectionMonitorService;
    private _company: Company;
    private _errorPages: ErrorPagesService;

    constructor(
        private injector: Injector,
        private router: Router
    ) {
    }

    private get authService(): AuthService {
        if (!this._authService) {
            this._authService = this.injector.get(AuthService);
        }
        return this._authService;
    }

    private get jwtService(): JwtService {
        if (!this._jwtService) {
            this._jwtService = this.injector.get(JwtService);
        }
        return this._jwtService;
    }

    private get connectionMonitor(): ConnectionMonitorService {
        if (!this._connectionMonitor) {
            this._connectionMonitor = this.injector.get(ConnectionMonitorService);
        }
        return this._connectionMonitor;
    }

    private get errorPages(): ErrorPagesService {
        if (!this._errorPages) {
            this._errorPages = this.injector.get(ErrorPagesService);
        }
        return this._errorPages;
    }

    private get companySwitched(): CompanySwitchedService {
        return this.injector.get(CompanySwitchedService);
    }

    private get company(): Company {
        if (!this._company) {
            this._company = this.injector.get(Company);
        }
        return this._company;
    }

    intercept(original: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // A v2 page's first load (asPageLoad) opens the error page when it fails.
        const req = markPageLoad(original);
        return this.send(req, next, this.jwtService.getToken(), true);
    }

    private send(req: HttpRequest<any>, next: HttpHandler, token: string, mayRefresh: boolean): Observable<any> {
        let request: HttpRequest<any> = req;

        // The JWT goes only to our API, never to another host (FE-10).
        if (token && isApiUrl(req.url)) {
            request = req.clone({headers: req.headers.set('Authorization', 'JWT ' + token)});
        }

        // The company this tab is in; the server answers 409 company_switched when the user has moved on.
        if (isApiUrl(req.url)) {
            const companyId = this.authService.getCurrentCompany()?.id;
            if (companyId) {
                request = request.clone({headers: request.headers.set('X-JFT-Company', String(companyId))});
            }
        }

        return next.handle(request)
          .pipe(
            // An answer from the server: the connection works, so earlier failures stop counting.
            tap((event) => {
              if (event instanceof HttpResponse) {
                this.connectionMonitor.recordSuccess();
              }
            }),
            catchError((e) => {
            // An access token that ran out (it lives 15 minutes): refresh once, shared by every
            // request that hit it (JwtService.refresh), and send the request again. Only a
            // refused refresh signs the user out.
            if (mayRefresh && e instanceof HttpErrorResponse && e.status === 401 && token
                && isApiUrl(req.url) && !isTokenEndpoint(req.url)) {
                return this.retryWithFreshToken(req, next, token, e);
            }
            return this.handleError(e, req);
          }));
    }

    private retryWithFreshToken(req: HttpRequest<any>, next: HttpHandler, used: string,
                                unauthorized: HttpErrorResponse): Observable<any> {
        const current = this.jwtService.getToken();
        // Another request refreshed while this one was out: just use the new token.
        const fresh$ = current && current !== used ? of(current) : this.jwtService.refresh();
        return fresh$.pipe(
            catchError((refreshError) => {
                if (refreshError instanceof HttpErrorResponse && (refreshError.status === 400 || refreshError.status === 401)) {
                    return this.handleError(unauthorized, req);
                }
                // The refresh did not reach the server (network, 5xx): no sign-out, report that.
                return this.handleError(refreshError, req);
            }),
            switchMap((fresh: string) => this.send(req, next, fresh, false)),
        );
    }

    /** Every error but the refreshable 401 (the body of the old catchError, unchanged). */
    private handleError(e: any, req: HttpRequest<any>): Observable<never> {
            if (environment.useSentry) {
              // Log to Sentry if enabled
            }
            if (e instanceof HttpErrorResponse) {
              // Handle maintenance mode - check for 503 status or maintenance_mode flag in response
              if (e.status === 503 || (e.error && e.error.maintenance_mode)) {
                this.errorPages.open('maintenance', { until: e.error?.maintenance_until });
                return throwError(() => e);
              }

              // Handle unauthorized access
              if (e.status === 401) {
                // Read the token before logout() clears it.
                onUnauthorized(this.router, this.authService, !!this.jwtService.getToken());
                return throwError(() => e);
              }

              // Switched to another company in another tab: Company Changed sheet, then Reload.
              const switchedTo = companySwitchedName(e);
              if (switchedTo !== null) {
                this.companySwitched.show(switchedTo);
                return throwError(() => e);
              }

              // Required Before Using the Platform (D-610): the company must sign a document first.
              // Classic pages too: the documents pages are v2 only.
              const documentUrl = documentRequiredUrl(e, this.router.url);
              if (documentUrl !== null) {
                // Read the profile first, so the shell hides the nav before the document page opens.
                this.authService.getProfile().subscribe({ complete: () => void this.router.navigateByUrl(documentUrl) });
                return throwError(() => e);
              }
              if (e.status === 403 && e.error?.code === 'document_required') {
                return throwError(() => e);
              }

              // The error pages (v2/errors/http-error-routing.ts): 429 always; 403, a tender's
              // 404, 500 and 502/504 only on a page load. The caller still gets the error.
              const page = errorPageFor(req, e, !!this.router.currentNavigation?.());
              if (page) {
                this.openErrorPage(page);
              }

              // 403 from a single data endpoint should not boot the user off the
              // page they're on; let the calling component decide how to render
              // the partial-access state. Auth failures already get caught above
              // via 401, and a fully-denied tender route is enforced by the
              // server returning 404 from get_comp_tender. Removing the global
              // 403→/tenders redirect prevents one denied resolver call from
              // navigating the entire app away.
              if (e.status === 403) {
                return throwError(() => e);
              }

              // Handle network errors (status 0) - backend unavailable, CORS, connection refused, etc.
              if (e.status === 0) {
                // The device itself has no network: say so, not "we cannot reach the server".
                // Never over unsaved input: the form's own error says the save failed.
                if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                  if (!hasUnsavedInput()) {
                    this.errorPages.open('offline');
                  }
                  return throwError(() => e);
                }
                if (isRateLimitedPath(req.url)) {
                  const from = this.errorPages.currentUrl();
                  return this.serverReachable().pipe(switchMap((reachable) => {
                    if (reachable) {
                      this.openErrorPage({ kind: 'too-many-requests', context: { status: 429, from } });
                      return throwError(() => new HttpErrorResponse({
                        status: 429, statusText: 'Too Many Requests', url: e.url ?? req.url,
                      }));
                    }
                    this.reportNetworkError(e);
                    return throwError(() => e);
                  }));
                }
                this.reportNetworkError(e);
                return throwError(() => e);
              }

              return throwError(() => e);
            }
            return throwError(() => e);
    }

    /**
     * After the current tick: a resolver that catches the same error and redirects (to /tenders,
     * /404, …) does so synchronously, so the error page, opened next, is the one that stays.
     * The page asked for is read now, before that redirect replaces it.
     */
    private openErrorPage(page: ErrorPageRoute): void {
        const from = page.context.from ?? this.errorPages.currentUrl();
        if (page.kind === 'connection') {
            // One 502/504 is not an outage: Connection Lost only when the health check fails too
            // (AACO F52). Otherwise the page keeps its own error and Try Again.
            this.connectionMonitor.confirmOutage({ ...page.context, from });
            return;
        }
        setTimeout(() => this.errorPages.open(page.kind, { ...page.context, from }), 0);
    }

    private reportNetworkError(e: HttpErrorResponse) {
        // Notify the connection monitor about the network error
        this.connectionMonitor.handleNetworkError(e);
        console.warn('Network error detected:', e);
    }

    // Straight to the backend, so the probe skips this interceptor and its token.
    private serverReachable(): Observable<boolean> {
        return new HttpClient(this.injector.get(HttpBackend))
            // No custom header: on the cross-origin API it needs a CORS preflight the server refuses.
            .get(`${environment.apiUrl}/system/health/`)
            .pipe(timeout(5000), map(() => true), catchError(() => of(false)));
    }
}

// Paths under Cloudflare rule "auth endpoints 10/min" (jft-us-1
// docs/runbooks/cloudflare-waf.md). Its block response has no CORS header, so
// the browser sees status 0; keep this list in step with the rule.
// The recovery-code and set-up-confirm paths (Plan 6 Task 6) are listed ahead
// of the rule: add them to rule 1 too (handed to Anton).
const RATE_LIMITED_PATHS = [
    '/auth/jwt/create/',
    '/auth/otp/totp/login/',
    '/auth/otp/totp/confirm/',
    '/auth/otp/static/login/',
    '/auth/otp/recovery/login/',
    '/auth/sso/company/delta/',
    '/auth/password/reset/',
    '/auth/users/activation/',
    '/auth/users/resend_activation/',
    '/user/register/',
    '/user/verify_email/',
    '/admin/login/',
    '/system/support/',
];

// The token endpoints themselves: a 401 there is the answer, never a reason to refresh.
const TOKEN_PATHS = ['/auth/jwt/create/', '/auth/jwt/refresh/', '/auth/jwt/verify/', '/auth/logout/'];

function isTokenEndpoint(url: string): boolean {
    const path = new URL(url, window.location.origin).pathname;
    return TOKEN_PATHS.some((prefix) => path.startsWith(prefix));
}

function isRateLimitedPath(url: string): boolean {
    const path = new URL(url, window.location.origin).pathname;
    return RATE_LIMITED_PATHS.some((prefix) => path.startsWith(prefix));
}

export const HttpInterceptorsProvider: ClassProvider = {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
};
